import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { MessageLogEntry, MessageStatus } from '@tailoring-crm/shared';
import { Badge, type BadgeProps, Button } from '@tailoring-crm/ui';
import { formatDateTime } from '../../lib/format.js';

/**
 * The message log, as the counter sees it.
 *
 * It answers one question — "did she get it?" — so the status is the loudest thing on each
 * row, and a message that is waiting says WHEN it will go rather than merely that it has not
 * gone. Dead-lettered rows carry the provider's error and a button, because by the time
 * somebody is reading this screen the cause has usually been fixed.
 */
const STATUS_VARIANT: Readonly<Record<MessageStatus, NonNullable<BadgeProps['variant']>>> = {
  queued: 'outline',
  scheduled: 'secondary',
  sent: 'default',
  delivered: 'success',
  read: 'success',
  failed: 'warning',
  dead_letter: 'destructive',
};

export interface MessageLogTableProps {
  entries: readonly MessageLogEntry[];
  retryingId: string | null;
  onRetry: (messageId: string) => void;
}

export function MessageLogTable({
  entries,
  retryingId,
  onRetry,
}: MessageLogTableProps): ReactElement {
  const { t } = useTranslation();

  return (
    <ul className="flex flex-col gap-2">
      {entries.map((entry) => (
        <li key={entry.id} className="flex flex-col gap-2 rounded-lg border border-border p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate font-medium">
                {entry.recipientName ? `${entry.recipientName} · ` : ''}
                {entry.to}
              </p>
              <p className="text-sm text-muted-foreground">
                {t(`notifications.event.${entry.event}`, {
                  defaultValue: entry.event,
                })}
                {entry.subjectLabel ? ` · ${entry.subjectLabel}` : ''}
                {entry.test ? ` · ${t('notifications.testBadge')}` : ''}
              </p>
            </div>
            <Badge variant={STATUS_VARIANT[entry.status]}>
              {t(`notifications.status.${entry.status}`)}
            </Badge>
          </div>

          {entry.renderedBody ? (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {entry.renderedBody}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>{formatDateTime(entry.createdAt)}</span>
            {entry.status === 'scheduled' && entry.nextAttemptAt ? (
              <span>
                {t('notifications.waitingUntil', { when: formatDateTime(entry.nextAttemptAt) })}
              </span>
            ) : null}
            {entry.readAt ? <span>{t('notifications.readAt')}</span> : null}
          </div>

          {entry.error ? <p className="text-sm text-destructive">{entry.error}</p> : null}

          {entry.status === 'dead_letter' || entry.status === 'failed' ? (
            <div>
              <Button
                size="sm"
                variant="outline"
                disabled={retryingId === entry.id}
                onClick={() => onRetry(entry.id)}
              >
                {t('notifications.retry')}
              </Button>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
